import { useState, useEffect, useRef } from 'react';
import { Button, Modal, ModalDialog, ModalClose, Typography, Stack, Input, FormControl, FormLabel, Link } from '@mui/joy';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';

export function PortionPicker({ originalServings, preferredServings, onServingsChange }) {
  const initialServings = preferredServings || originalServings;
  const [open, setOpen] = useState(false);
  const [currentServings, setCurrentServings] = useState(initialServings);
  const [tempServings, setTempServings] = useState(initialServings);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!hasInitialized.current && preferredServings && preferredServings !== originalServings) {
      setCurrentServings(preferredServings);
      const scalingFactor = preferredServings / originalServings;
      onServingsChange(scalingFactor);
      hasInitialized.current = true;
    }
  }, [preferredServings, originalServings, onServingsChange]);

  const handleOpen = () => {
    setTempServings(currentServings);
    setOpen(true);
  };

  const handleClose = () => {
    setTempServings(currentServings);
    setOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (tempServings > 0) {
      setCurrentServings(tempServings);
      const scalingFactor = tempServings / originalServings;
      onServingsChange(scalingFactor);
      setOpen(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleIncrement = () => {
    setTempServings(prev => Math.max(0.5, prev + 0.5));
  };

  const handleDecrement = () => {
    setTempServings(prev => Math.max(0.5, prev - 0.5));
  };

  const handleUseOriginal = () => {
    setTempServings(originalServings);
  };

  const handleUsePreferred = () => {
    if (preferredServings) {
      setTempServings(preferredServings);
    }
  };

  return (
    <>
      <Button 
        size="sm" 
        variant="outlined" 
        color="neutral"
        onClick={handleOpen}
        startDecorator={<PeopleOutlinedIcon sx={{ fontSize: '14px' }} />}
        sx={{ fontSize: '12px' }}
      >
        {currentServings} serving{currentServings === 1 ? '' : 's'}
      </Button>

      <Modal open={open} onClose={handleClose} disableRestoreFocus >
        <ModalDialog sx={{ maxWidth: '400px', width: '90%' }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 1 }}>
            Scale Recipe Servings
          </Typography>
          <form onSubmit={handleSubmit}>
            <Stack spacing={1}>
              <FormControl>
                <FormLabel>Desired Servings</FormLabel>
                <Input
                  type="number"
                  value={tempServings}
                  autoFocus
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value) && value > 0) {
                      setTempServings(value);
                    } else if (e.target.value === '') {
                      setTempServings('');
                    }
                  }}
                  onKeyDown={handleKeyDown}
                  slotProps={{
                    input: {
                      min: 0.5,
                      step: 0.5,
                    }
                  }}
                  endDecorator={
                    <Stack direction="row" gap={0.5}>
                      <Button
                        size="sm"
                        variant="plain"
                        color="neutral"
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          handleDecrement();
                        }}
                        sx={{ minWidth: '32px', p: 0.5 }}
                      >
                        −
                      </Button>
                      <Button
                        size="sm"
                        variant="plain"
                        color="neutral"
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          handleIncrement();
                        }}
                        sx={{ minWidth: '32px', p: 0.5 }}
                      >
                        +
                      </Button>
                    </Stack>
                  }
                />
              </FormControl>
              <Stack direction="column" gap={1} flexWrap="wrap" alignItems="flex-start" sx={{ p: 1, px: 1.5, backgroundColor: 'var(--joy-palette-neutral-100)', borderRadius: 'sm' }}>
                <Stack direction="row" gap={0.5}>
                  <Typography level="body-sm" sx={{ width: '115px' }}>Original recipe:</Typography>
                  <Link
                    component="button"
                    type="button"
                    onClick={handleUseOriginal}
                    level="body-sm"
                    sx={{ cursor: 'pointer' }}
                  >
                    {originalServings} serving{originalServings === 1 ? '' : 's'}
                  </Link>
                </Stack>
                <Stack direction="row" gap={0.5}>
                  <Typography level="body-sm" sx={{ width: '115px' }}>Household size:</Typography>
                  <Link
                    component="button"
                    type="button"
                    onClick={handleUsePreferred}
                    level="body-sm"
                    sx={{ cursor: 'pointer' }}
                  >
                    {preferredServings} serving{preferredServings === 1 ? '' : 's'}
                  </Link>
                </Stack>                
              </Stack>              
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button variant="outlined" color="neutral" type="button" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={!tempServings || tempServings <= 0} sx={{ px: 3 }}>
                Apply
              </Button>
            </Stack>
          </form>
        </ModalDialog>
      </Modal>
    </>
  );
}

